import React, { useCallback, useRef, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ReactCropperElement } from "react-cropper";
import { useSelector } from "react-redux";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";

// Types
import type { IOption } from "models/Form";
import type { IFRAttachment } from "models/HaulPay";
import { FileProgress, FileUploadProvider, useUpload } from "context/FileUpload";
import { selectUserDetails } from "store/user/selector";
import { UPLOAD_DOCUMENT_URLS } from "constants/routes";
import { showCustomActionSheet } from "components/CustomActionSheet";

// Utils
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { UPLOAD_CATEGORY } from "constants/base";

// Components
import { FileUploader } from "./components/FileUploader";
import { CropModal } from "./components/CropModal";
import { DocumentPreviewModal } from "./components/DocumentPreviewModal";

// Hooks
import { useFundingRequestForm } from "./FundingRequestFormContext";
import { useUserFeature } from "hooks/useUserFeature";

// Uploader
import ChunkedUploader from "../../../../../../common/ChunkUploader";

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILE_SIZE_UPLOAD = 25 * 1024 * 1024; // 25MB
const FILE_SIZE_LIMIT_ERROR =
  "This file exceeds the 25MB limit. Please reduce the file size and try again.";

const UploadDocumentsCard: React.FC<{ fundingRequestId?: string }> = ({
  fundingRequestId: propFundingRequestId
}) => {
  // Router + external state
  const params = useParams<{ fundingRequestId: string }>();
  const fundingRequestId = propFundingRequestId ?? params.fundingRequestId;

  if (!fundingRequestId) {
    return (
      <div className="bg-card border border-card rounded-lg p-6">
        <div className="text-error font-semibold">Error: Missing funding request ID</div>
      </div>
    );
  }
  const {
    allowDocsUpdate,
    loading,
    attachments,
    handleAttachmentsChange,
    editingAttachment,
    setEditingAttachment,
    selectedFR,
    isAssignedLoadsPage
  } = useFundingRequestForm();
  const userDetails = useSelector(selectUserDetails);
  const { isCarrier } = useUserFeature();

  // Upload context
  const {
    files,
    addFile,
    setFileProgress,
    setFileStatus,
    removeFile,
    updateFile,
    replaceFile,
    updateFileCategory,
    updateFileVisibility
  } = useUpload();

  // Local UI state
  const [showDocumentModal, setShowDocumentModal] = React.useState(false);
  const [isDragActive, setIsDragActive] = React.useState(false);
  const [showCropModal, setShowCropModal] = React.useState(false);
  const [currentFileIndex, setCurrentFileIndex] = React.useState<number | null>(null);
  const [hasBeenCropped, setHasBeenCropped] = React.useState(false);
  const [isCompressing, setIsCompressing] = React.useState(false);
  const [compressionProgress, setCompressionProgress] = React.useState(0);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isSubmittingFile, setIsSubmittingFile] = React.useState(false);

  // Refs
  const cropperRef = useRef<ReactCropperElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachmentsRef = useRef<IFRAttachment[]>(attachments);
  const processedAttachmentRef = useRef<string | null>(null);
  const pendingEditFileIdRef = useRef<string | null>(null);
  const submissionInProgressRef = useRef(false);

  useEffect(() => {
    attachmentsRef.current = attachments;
  }, [attachments]);

  // Handle editing existing attachments
  useEffect(() => {
    if (editingAttachment && processedAttachmentRef.current !== editingAttachment.id) {
      processedAttachmentRef.current = editingAttachment.id;

      const previewUrl =
        editingAttachment.previewUrl ||
        editingAttachment.preview_url ||
        editingAttachment.downloadUrl ||
        editingAttachment.download_url;

      if (!previewUrl) {
        showErrorMessage("Cannot edit attachment: No preview available");
        setEditingAttachment(null);
        return;
      }

      // Check if it's an image or PDF
      const filename = editingAttachment.filename || "";
      const extension = filename.split(".").pop()?.toLowerCase();
      const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");
      const isPDF = extension === "pdf";

      if (!isImage && !isPDF) {
        showErrorMessage("Can only edit image and PDF attachments");
        setEditingAttachment(null);
        return;
      }

      // Create a virtual file from the attachment for editing
      fetch(previewUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const defaultFilename = isImage ? "image.jpg" : "document.pdf";
          const defaultType = isImage ? "image/jpeg" : "application/pdf";
          const file = new File([blob], editingAttachment.filename || defaultFilename, {
            type: blob.type || defaultType
          });
          const fileProgress = addFile(file);

          // Set the existing category and visibility
          const categoryStr =
            typeof editingAttachment.category === "string"
              ? editingAttachment.category
              : Array.isArray(editingAttachment.category)
                ? editingAttachment.category.join(",")
                : "";

          const visibleTo = editingAttachment.visibleTo || editingAttachment.visible_to || [];

          updateFileCategory(fileProgress.id, categoryStr);
          updateFileVisibility(fileProgress.id, Array.isArray(visibleTo) ? visibleTo : []);

          // Set preview and open modal
          updateFile(fileProgress.id, { preview: previewUrl });

          // Store the file ID so the next useEffect can open the modal
          pendingEditFileIdRef.current = fileProgress.id;
          setIsEditMode(true);
        })
        .catch((_err) => {
          showErrorMessage("Failed to load attachment for editing");
          setEditingAttachment(null);
          processedAttachmentRef.current = null;
        });
    } else if (!editingAttachment) {
      // Reset the ref when editingAttachment is cleared
      processedAttachmentRef.current = null;
    }
  }, [
    editingAttachment,
    setEditingAttachment,
    addFile,
    updateFileCategory,
    updateFileVisibility,
    updateFile
  ]);

  // Watch for the pending file to appear in the files array and open the appropriate modal
  useEffect(() => {
    if (pendingEditFileIdRef.current && files.length > 0) {
      const fileIndex = files.findIndex((f) => f.id === pendingEditFileIdRef.current);
      if (fileIndex !== -1) {
        const file = files[fileIndex];
        setCurrentFileIndex(fileIndex);
        // Open the appropriate modal based on file type
        if (file.type?.startsWith("image/")) {
          setShowCropModal(true);
        } else {
          setShowDocumentModal(true);
        }
        pendingEditFileIdRef.current = null; // Clear the pending ID
      }
    }
  }, [files]);

  const pushAttachment = useCallback(
    (incoming: IFRAttachment) => {
      const map = new Map<string, IFRAttachment>();
      attachmentsRef.current.forEach((att) => {
        map.set(String(att.id), att);
      });
      map.set(String(incoming.id), incoming);
      const merged = Array.from(map.values());
      attachmentsRef.current = merged;
      handleAttachmentsChange(merged);
    },
    [handleAttachmentsChange]
  );

  const toAttachment = useCallback((file: FileProgress, previewUrl?: string): IFRAttachment => {
    const category = file.categories || "";
    const visibleList = Array.isArray(file.visible_to) ? file.visible_to : [];

    const attachment: IFRAttachment = {
      id: String(file.id),
      filename: file.name ?? "Unnamed file",
      category,
      uploaded: new Date().toISOString(),
      visibleTo: visibleList,
      visible_to: visibleList,
      downloadUrl: previewUrl ?? "",
      download_url: previewUrl ?? "",
      previewUrl: previewUrl,
      preview_url: previewUrl,
      type: file.fileType ?? file.type,
      size: file.fileSize ?? file.size
    };

    return attachment;
  }, []);

  // Derived
  const categories = useMemo(() => UPLOAD_CATEGORY, []);
  const isUploading = useMemo(
    () => isSubmittingFile || files.some((f) => f.status === "uploading"),
    [files, isSubmittingFile]
  );
  const currentFile = useMemo<FileProgress | null>(
    () => (currentFileIndex !== null ? files[currentFileIndex] : null),
    [currentFileIndex, files]
  );

  // Map FileProgress.categories (string) -> IOption (if present in categories)
  const currentCategoryOptions = useMemo<IOption[]>(() => {
    if (!currentFile) return [];
    const raw = ((currentFile as any).categories as string | undefined) ?? "";
    const values = raw
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!values.length) return [];

    return values
      .map(
        (candidate) =>
          categories.find(
            (opt) =>
              String((opt as any).value ?? (opt as any).label ?? "")
                .toLowerCase()
                .trim() === candidate.toLowerCase()
          ) as IOption | undefined
      )
      .filter((opt): opt is IOption => Boolean(opt));
  }, [currentFile, categories]);

  // Add a file to queue (returns FileProgress from the context)
  const addFileToUpload = useCallback((file: File): FileProgress => addFile(file), [addFile]);

  // Drag hover
  const handleDrag = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const isDragEvent = e.type === "dragenter" || e.type === "dragover";
      setIsDragActive(isDragEvent && allowDocsUpdate && !loading);
    },
    [allowDocsUpdate, loading]
  );

  const processFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const filesArray = Array.from(newFiles);
      const valid = filesArray.filter((f) => f.size <= MAX_FILE_SIZE);

      if (valid.length < filesArray.length) {
        showErrorMessage(FILE_SIZE_LIMIT_ERROR);
      }

      // capture starting index to point cropper correctly
      let nextIndexBase = files.length;

      valid.forEach((file) => {
        const isImage = file.type.startsWith("image/");
        const fileProgress = addFileToUpload(file);

        // Create preview URL for all files (needed for PDFs)
        try {
          const previewUrl = URL.createObjectURL(file);
          updateFile(fileProgress.id, { preview: previewUrl });
        } catch (error) {
          // console.error("Failed to create preview URL:", error);
        }

        // Set current file index and show appropriate modal based on file type
        setCurrentFileIndex(nextIndexBase);

        // Reset crop flag for new file
        setHasBeenCropped(false);

        if (isImage) {
          setShowCropModal(true);
        } else {
          setShowDocumentModal(true);
        }
        nextIndexBase += 1;
      });
    },
    [addFileToUpload, files.length, updateFile]
  );
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
        // reset input to allow re-adding same file
        e.target.value = "";
      }
    },
    [processFiles]
  );

  const isMobileBrowser = useCallback(() => {
    if (typeof window === "undefined") return false;
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent.toLowerCase()
    );
  }, []);

  const handleCamera = useCallback(
    async (source: CameraSource) => {
      try {
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Uri,
          source,
          saveToGallery: false
        });

        if (!photo.webPath) {
          throw new Error("No photo was returned");
        }

        const path = photo.webPath.startsWith("file:")
          ? Capacitor.convertFileSrc(photo.webPath)
          : photo.webPath;
        const response = await fetch(path);
        const blob = await response.blob();
        const format = photo.format ?? "jpeg";
        const file = new File([blob], `photo.${format}`, { type: `image/${format}` });

        const simulatedEvent = {
          target: {
            files: [file],
            value: ""
          }
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        handleFileInputChange(simulatedEvent);
      } catch (error) {
        showErrorMessage(
          `Camera error: ${error instanceof Error ? error.message : "Failed to get photo"}`
        );
      }
    },
    [handleFileInputChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragActive(false);
      if (!allowDocsUpdate || loading) return;

      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length) processFiles(dropped);
    },
    [allowDocsUpdate, loading, processFiles]
  );

  const initializeUploader = useCallback(
    (file: FileProgress) => {
      if (file.size > MAX_FILE_SIZE_UPLOAD) {
        return showErrorMessage(FILE_SIZE_LIMIT_ERROR);
      }
      const fileObj = file.file;
      return new ChunkedUploader({
        url: "/upload/chunk/",
        urlCallback: `${
          UPLOAD_DOCUMENT_URLS({
            factoring_id: userDetails.factoring_id,
            funding_request_id: fundingRequestId
          }).FR_ATTACHMENT
        }/${file.id}`,
        file: fileObj,
        onProgress: (progress: number) => setFileProgress(file.id, progress)
      });
    },
    [setFileProgress, userDetails.factoring_id, fundingRequestId]
  );

  const uploadFile = useCallback(
    async (file: FileProgress) => {
      try {
        setFileStatus(file.id, "uploading");

        const uploader = initializeUploader(file);
        if (!uploader) throw new Error("Failed to initialize file uploader");

        const result = await uploader.upload();

        if (result.success) {
          // First update the file status to completed
          setFileStatus(file.id, "completed");
          updateFile(file.id, { preview: result.previewUrl });

          const nextAttachment = toAttachment(file, result.previewUrl);
          pushAttachment(nextAttachment);

          // Show success message
          showSuccessMessage(`File "${file.name}" uploaded successfully`);

          // Remove the file from the list after a brief delay (for UX feedback)
          setTimeout(() => {
            removeFile(file.id);
          }, 1500);

          return { success: true };
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to upload file";
        setFileStatus(file.id, "error");
        showErrorMessage(`Failed to upload "${file.name}": ${message}`);
        return { success: false, error: message };
      }
    },
    [initializeUploader, setFileStatus, updateFile, removeFile, pushAttachment, toAttachment]
  );

  const handleClick = useCallback(async () => {
    if (!allowDocsUpdate || loading || isUploading) {
      return;
    }

    if (Capacitor.getPlatform() === "web" && !isMobileBrowser()) {
      fileInputRef.current?.click();
      return;
    }

    const { index } = await showCustomActionSheet({
      title: "Upload Options",
      message: "Select a source",
      options: [
        { title: "Take Photo", icon: "camera" },
        { title: "Choose from Gallery", icon: "images" },
        { title: "Browse Files", icon: "folder" },
        { title: "Cancel", style: "cancel" }
      ],
      displayMode: "auto"
    });

    if (index === 0) {
      await handleCamera(CameraSource.Camera);
    } else if (index === 1) {
      await handleCamera(CameraSource.Photos);
    } else if (index === 2) {
      fileInputRef.current?.click();
    }
  }, [allowDocsUpdate, handleCamera, isMobileBrowser, loading, isUploading]);

  const handleCrop = useCallback(async () => {
    if (!currentFile || !cropperRef.current?.cropper) {
      showErrorMessage("No image to crop");
      return false;
    }
    const cf = currentFile; // non-null in this scope
    // Uncapped, this rasterizes the full camera resolution -- ~12MP on a modern phone -- and
    // getCroppedCanvas is synchronous, so it blocks the main thread before toBlob even starts.
    // Same caps ChunkUploadFile already uses.
    const canvas = cropperRef.current.cropper.getCroppedCanvas({
      maxWidth: 2048,
      maxHeight: 2048,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high"
    });
    if (!canvas) {
      showErrorMessage("Failed to crop image");
      return false;
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.9)
    );
    if (!blob) {
      showErrorMessage("Failed to crop image");
      return false;
    }

    const newFile = new File([blob], cf.name, {
      type: "image/jpeg",
      lastModified: Date.now()
    });

    replaceFile(cf.id, newFile);
    setHasBeenCropped(true);

    try {
      if (cf.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(cf.preview);
      }
      const previewUrl = URL.createObjectURL(blob);
      updateFile(cf.id, { preview: previewUrl });
    } catch (error) {
      // Ignore preview URL errors
    }
    return true;
  }, [currentFile, replaceFile, updateFile]);

  // Check if image needs compression
  const shouldCompressImage = useCallback(async (file: File): Promise<boolean> => {
    const fileSizeInMB = file.size / (1024 * 1024);

    // Skip compression for very small files (< 200KB)
    if (fileSizeInMB < 0.2) {
      return false;
    }

    // Always compress large files (> 1MB)
    if (fileSizeInMB > 1) {
      return true;
    }

    // For medium files, check dimensions
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        // Compress if either dimension > 2000px
        const needsCompression = img.width > 2000 || img.height > 2000;
        URL.revokeObjectURL(img.src);
        resolve(needsCompression);
      };
      img.onerror = () => {
        // If can't read dimensions, compress if file is large enough
        resolve(fileSizeInMB > 0.5);
      };
      img.src = URL.createObjectURL(file);
    });
  }, []);

  // Compress image file for upload (high quality for text preservation)
  const compressImageFile = useCallback(
    async (file: File): Promise<File> => {
      try {
        // Check if compression is needed
        const needsCompression = await shouldCompressImage(file);

        if (!needsCompression) {
          return file;
        }

        setIsCompressing(true);
        setCompressionProgress(0);
        // Dynamic import to reduce initial bundle size
        const imageCompression = (await import("browser-image-compression")).default;

        const options = {
          maxSizeMB: 1, // Max file size in MB
          maxWidthOrHeight: 3000, // Max dimension (preserves large invoices)
          useWebWorker: true, // Use worker for better performance
          initialQuality: 0.92, // High quality for text
          alwaysKeepResolution: false, // Allow resizing if needed
          fileType: "image/jpeg", // Convert to JPEG for better compression
          onProgress: (progress: number) => {
            setCompressionProgress(Math.round(progress));
          }
        };

        const compressedFile = await imageCompression(file, options);

        setIsCompressing(false);
        setCompressionProgress(100);

        return compressedFile;
      } catch (error) {
        setIsCompressing(false);
        setCompressionProgress(0);
        // Fallback to original file if compression fails
        return file;
      }
    },
    [shouldCompressImage]
  );

  // Upload current file from modal
  const handleUploadCurrentFile = useCallback(async () => {
    if (!currentFile || submissionInProgressRef.current) return;

    // Validate required fields before upload
    const categories = (currentFile as any).categories || "";
    if (!categories || categories.trim() === "") {
      showErrorMessage("Please select a category before uploading");
      return;
    }

    // Validate visible to field (only for non-carriers)
    if (!isCarrier) {
      const visibleTo = (currentFile as any).visible_to || [];
      if (!visibleTo || visibleTo.length === 0) {
        showErrorMessage("Please select who can view this document");
        return;
      }
    }

    submissionInProgressRef.current = true;
    setIsSubmittingFile(true);

    try {
      if (!hasBeenCropped && currentFile.type?.startsWith("image/")) {
        // Compress non-cropped images (PNG, JPEG, etc.)
        try {
          const compressed = await compressImageFile(currentFile.file);

          // Directly mutate the currentFile object's file property
          (currentFile as any).file = compressed;

          // Also update in context for UI
          replaceFile(currentFile.id, compressed);
        } catch (error) {
          showErrorMessage("Failed to compress image");
        }
      }

      const result = await uploadFile(currentFile);

      if (result.success) {
        // If in edit mode, delete the old attachment from the backend
        if (isEditMode && editingAttachment && selectedFR?.id) {
          try {
            // Wait a moment for the new attachment to be added to state
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Import the delete API at the top if not already imported
            const { deleteFRAttachmentAPI } = await import("services/api/haulpay");
            await deleteFRAttachmentAPI(selectedFR.id, editingAttachment.id);

            // Remove the old attachment from state using the ref (which has the latest state)
            const updatedAttachments = attachmentsRef.current.filter(
              (att) => att.id !== editingAttachment.id
            );
            handleAttachmentsChange(updatedAttachments);

            showSuccessMessage("Attachment updated successfully");
          } catch (error) {
            showErrorMessage("Updated attachment uploaded, but failed to remove old version");
          }
          setEditingAttachment(null);
          setIsEditMode(false);
          processedAttachmentRef.current = null;
        }

        // Close modals after successful upload
        setShowCropModal(false);
        setShowDocumentModal(false);
        setCurrentFileIndex(null);
        setHasBeenCropped(false); // Reset for next file
      }
    } finally {
      submissionInProgressRef.current = false;
      setIsSubmittingFile(false);
    }
  }, [
    currentFile,
    uploadFile,
    replaceFile,
    updateFile,
    compressImageFile,
    hasBeenCropped,
    isCarrier,
    isEditMode,
    editingAttachment,
    attachments,
    handleAttachmentsChange,
    setEditingAttachment,
    selectedFR
  ]);

  if (loading) {
    return (
      <div className="bg-card border border-card rounded-lg p-3">
        <h3 className="text-primary font-semibold mb-2">Upload Documents</h3>
        <div className="animate-pulse">
          <div className="h-28 bg-foreground rounded-md"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <h2 className="text-lg font-medium mb-4">Upload Documents</h2>
      {currentFile && currentFile.type?.startsWith("image/") && (
        <CropModal
          isOpen={showCropModal}
          onClose={() => {
            setShowCropModal(false);
            if (isEditMode) {
              setEditingAttachment(null);
              setIsEditMode(false);
              processedAttachmentRef.current = null;
              // Remove the temporary file from upload queue
              if (currentFile) {
                removeFile(currentFile.id);
              }
              setCurrentFileIndex(null);
            }
          }}
          // Pass the exact shape CropModal expects
          file={{
            id: currentFile.id,
            preview: currentFile.preview ?? "", // ensure string
            category: currentCategoryOptions,
            name: currentFile.name ?? "",
            visibleTo: (currentFile as any).visible_to || []
          }}
          categories={categories}
          onCategoryChange={(id: string, categories: IOption[]) => {
            // Extract values from the array of options
            const values = categories.map((option) => option.value);

            // If there's at least one category selected, use it
            // Otherwise use empty string as default
            const value = values.length > 0 ? values.join(",") : "";

            // Pass the value to updateFileCategory
            updateFileCategory(id, value);
          }}
          onCrop={handleCrop}
          cropperRef={cropperRef}
          onVisibilityChange={updateFileVisibility}
          onUpload={handleUploadCurrentFile}
          isUploading={isSubmittingFile || currentFile.status === "uploading"}
          uploadProgress={currentFile.progress || 0}
          isCompressing={isCompressing}
          compressionProgress={compressionProgress}
          isAssignedLoadsPage={isAssignedLoadsPage}
        />
      )}
      {currentFile && !currentFile.type?.startsWith("image/") && (
        <DocumentPreviewModal
          isOpen={showDocumentModal}
          onClose={() => {
            setShowDocumentModal(false);
            if (isEditMode) {
              setEditingAttachment(null);
              setIsEditMode(false);
              processedAttachmentRef.current = null;
              // Remove the temporary file from upload queue
              if (currentFile) {
                removeFile(currentFile.id);
              }
              setCurrentFileIndex(null);
            }
          }}
          file={{
            id: currentFile.id,
            name: currentFile.name ?? "",
            category: currentCategoryOptions,
            type: currentFile.type,
            preview: currentFile.preview,
            visibleTo: (currentFile as any).visible_to || []
          }}
          categories={categories}
          onCategoryChange={(id: string, categories: IOption[]) => {
            // Extract values from the array of options
            const values = categories.map((option) => option.value);

            // If there's at least one category selected, use it
            // Otherwise use empty string as default
            const value = values.length > 0 ? values.join(",") : "";

            // Pass the value to updateFileCategory
            updateFileCategory(id, value);
          }}
          onVisibilityChange={updateFileVisibility}
          onUpload={handleUploadCurrentFile}
          isUploading={isSubmittingFile || currentFile.status === "uploading"}
          uploadProgress={currentFile.progress || 0}
          isCompressing={isCompressing}
          compressionProgress={compressionProgress}
          isAssignedLoadsPage={isAssignedLoadsPage}
        />
      )}
      <FileUploader
        isDragActive={isDragActive}
        isGlobalDisabled={!allowDocsUpdate}
        loading={loading}
        isUploading={isUploading}
        onDrag={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        inputRef={fileInputRef}
        onChange={handleFileInputChange}
      />
    </div>
  );
};

// Wrap with provider
const UploadDocumentsCardWithProvider: React.FC<{ fundingRequestId?: string }> = ({
  fundingRequestId
}) => (
  <FileUploadProvider>
    <UploadDocumentsCard fundingRequestId={fundingRequestId} />
  </FileUploadProvider>
);

export default UploadDocumentsCardWithProvider;
